import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllPatients,
  clearPatients,
  updatePatient,
  deletePatient,
  fetchPatientVitalsHistory,
  clearVitalsHistory,
} from "../../redux/slices/PatientSlice";
import "./AdminCSS/Details.css";
import PatientUpdate from "./patientupdate";

const historyOptions = [
  { label: "Last 1 Day", value: "1d" },
  { label: "Last 1 Week", value: "1w" },
  { label: "Last 2 Weeks", value: "2w" },
  { label: "Last 3 Weeks", value: "3w" },
];

const PatientDetails = () => {
  const dispatch = useDispatch();

  // Redux states
  const {
    patients,
    loading,
    error,
    vitalsHistory,
    vitalsHistoryLoading,
    vitalsHistoryError,
  } = useSelector((state) => state.patients);

  // Patient update form state
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    age: "",
    gender: "",
  });

  // Modal state for history view
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyPatient, setHistoryPatient] = useState(null);
  const [selectedHistoryRange, setSelectedHistoryRange] = useState("1w");

  // Fetch patients on mount
  useEffect(() => {
    dispatch(fetchAllPatients());
    return () => {
      dispatch(clearPatients());
      dispatch(clearVitalsHistory());
    };
  }, [dispatch]);

  // Patient update handlers
  const handleUpdateClick = (patient) => {
    setSelectedPatient(patient);
    setFormData({
      name: patient.patient_name || "",
      age: patient.age || "",
      gender: patient.gender || "",
    });
  };

  const handleUpdateSubmit = () => {
    if (selectedPatient) {
      dispatch(
        updatePatient({
          patient_id: selectedPatient.patient_id,
          updatedData: formData,
        })
      );
      setSelectedPatient(null);
      setFormData({ name: "", age: "", gender: "" }); // Reset form
    }
  };

  const handleDelete = (patientId) => {
    if (window.confirm("Are you sure you want to delete this patient?")) {
      dispatch(deletePatient(patientId));
    }
  };

  // History modal handlers
  const handleViewHistoryClick = (patient) => {
    setHistoryPatient(patient);
    setSelectedHistoryRange("1w");
    setHistoryModalOpen(true);
    dispatch(clearVitalsHistory());
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalOpen(false);
    setHistoryPatient(null);
    dispatch(clearVitalsHistory());
  };

  // Fetch vitals history from API for selected patient and duration
  const handleFetchHistory = () => {
    if (historyPatient && selectedHistoryRange) {
      console.log(
        "Dispatching fetchPatientVitalsHistory for patient_id:",
        historyPatient.patient_id,
        "duration:",
        selectedHistoryRange
      );
      dispatch(
        fetchPatientVitalsHistory({
          patient_id: historyPatient.patient_id,
          duration: selectedHistoryRange,
        })
      );
    }
  };

  return (
    <div className="Devices-page">
      {loading && <p className="loading-text">Loading...</p>}
      {error && (
        <p className="error-text">
          Error: Unable to fetch patients. Please try again later.
        </p>
      )}

      {!loading && !error && patients.length > 0 && (
        <div className="table-scroll-container">
          <table className="device-table">
            <thead>
              <tr>
                <th>Patient_ID</th>
                <th>Name</th>
                <th>Age</th>
                <th>Gender</th>
                <th>Operations</th>
              </tr>
            </thead>
            <tbody>
              {patients.map((patient) => (
                <tr key={patient.patient_id}>
                  <td>{patient.patient_id}</td>
                  <td>{patient.patient_name}</td>
                  <td>{patient.age}</td>
                  <td>{patient.gender}</td>
                  <td className="operations">
                    {/* 🔥 VIEW BUTTON */}
                    <button
                      className="view-btn"
                      onClick={() => handleViewHistoryClick(patient)}
                    >
                      View
                    </button>
                    {/* 🔥 UPDATE BUTTON - UNCOMMENTED & ENABLED */}
                    <button 
                      className="update-btn" 
                      onClick={() => handleUpdateClick(patient)}
                    >
                      Update
                    </button>
                    {/* 🔥 DELETE BUTTON - UNCOMMENTED & ENABLED */}
                    <button 
                      className="delete-btn" 
                      onClick={() => handleDelete(patient.patient_id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && patients.length === 0 && (
        <p className="empty-text">No patients found.</p>
      )}

      {/* 🔥 PATIENT UPDATE MODAL */}
      {selectedPatient && (
        <PatientUpdate
          formData={formData}
          setFormData={setFormData}
          handleUpdateSubmit={handleUpdateSubmit}
          setSelectedPatient={setSelectedPatient}
        />
      )}

      {/* Modal for history view */}
      {historyModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content modal-lg">
            <button
              className="close-modal-btn"
              onClick={handleCloseHistoryModal}
            >
              &times;
            </button>
            <h3>
              Select History Duration for&nbsp;
              {historyPatient?.patient_name}
            </h3>
            <div>
              <label>
                Duration:&nbsp;
                <select
                  value={selectedHistoryRange}
                  onChange={(e) => setSelectedHistoryRange(e.target.value)}
                  className="history-dropdown"
                >
                  {historyOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="view-history-btn"
                style={{ marginLeft: 12 }}
                onClick={handleFetchHistory}
                disabled={vitalsHistoryLoading}
              >
                {vitalsHistoryLoading ? "Loading..." : "Fetch History"}
              </button>
            </div>

            {/* Show error */}
            {vitalsHistoryError && (
              <p className="error-text mt-2">Error: {vitalsHistoryError}</p>
            )}

            {/* Show history table */}
            {!vitalsHistoryLoading && vitalsHistory?.length > 0 && (
              <div
                className="table-scroll-container"
                style={{ maxHeight: 400, marginTop: 16 }}
              >
                <table className="device-table">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Temperature</th>
                      <th>Heart Rate</th>
                      <th>Blood Pressure</th>
                      <th>Step Count</th>
                      <th>SpO2</th>
                      <th>Battery Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vitalsHistory.map((record) => (
                      <tr key={record.timestamp}>
                        <td>{new Date(record.timestamp).toLocaleString()}</td>
                        <td>{record.temperature ?? "-"}</td>
                        <td>{record.heart_rate ?? "-"}</td>
                        <td>
                          {record.systolic && record.diastolic
                            ? `${record.systolic}/${record.diastolic}`
                            : "-"}
                        </td>
                        <td>{record.steps_count ?? "-"}</td>
                        <td>{record.spo2 ?? "-"}</td>
                        <td>{record.battery_level ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* No data message */}
            {!vitalsHistoryLoading &&
              !vitalsHistoryError &&
              vitalsHistory.length === 0 && (
                <p className="empty-text mt-3">No historical data found.</p>
              )}

            <button
              className="close-history-btn mt-3"
              onClick={handleCloseHistoryModal}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientDetails;